import { useMemo } from 'react';

import { TDifferenceType } from '@entities/purchase/model/index.types';

export const useGetChartBarsPercent = ({
    budgetBalance,
    basicRemainCost,
    differenceSum,
    differenceType,
}: {
    budgetBalance: number;
    basicRemainCost: number;
    differenceSum: number;
    differenceType: TDifferenceType;
}) => {
    const { budgetBalancePercent, basicRemainCostPercent, differencePercent } = useMemo(() => {
        const safeBudget = budgetBalance ?? 0; // остаток бюджета
        const safeRemain = basicRemainCost ?? 0; // требуется на закупки
        const safeDiff = differenceSum ?? 0; // разница (перерасход или экономия)

        let budgetPercent = 0; // процент остаток бюджета
        let remainPercent = 0; // процент требуется на закупки

        if (safeBudget <= 0 && safeRemain <= 0) {
            budgetPercent = 0;
            remainPercent = 0;
        } else if (safeBudget >= safeRemain) {
            budgetPercent = 100;
            remainPercent = safeBudget ? (safeRemain / safeBudget) * 100 : 0;
        } else {
            remainPercent = 100;
            budgetPercent = safeRemain ? (safeBudget / safeRemain) * 100 : 0;
        }

        let diffPercent = 0;

        if (safeDiff > 0) {
            if (differenceType === 'overspending') {
                diffPercent = Math.max(100 - budgetPercent, 0);
            } else if (differenceType === 'saving') {
                diffPercent = Math.max(100 - remainPercent, 0);
            }
        }

        // костыль для кейса, когда перерасход БОЛЬШЕ, чем требуется на закупки
        if (differenceType === 'overspending' && safeDiff > safeRemain) {
            remainPercent = (safeRemain / safeDiff) * 100;
        }

        return {
            budgetBalancePercent: budgetPercent,
            basicRemainCostPercent: remainPercent,
            differencePercent: diffPercent,
        };
    }, [budgetBalance, basicRemainCost, differenceSum, differenceType]);

    const getBudgetBalanceChartWidth = () => {
        if (differenceType === 'overspending') {
            if (differencePercent < 1.5 && differenceSum > 0) {
                return `calc(100% - 5px)`;
            }
        }
        return `${budgetBalancePercent}%`;
    };
    const getBasicRemainCostChartWidth = () => {
        if (differenceType === 'saving') {
            if (differencePercent < 1.5 && differenceSum > 0) {
                return `calc(100% - 5px)`;
            }
        }
        return `${basicRemainCostPercent}%`;
    };
    const getDiffChartWidth = () => {
        if (differencePercent > 99) {
            return `calc(100% - 5px)`;
        }
        return `${differencePercent}%`;
    };

    return {
        budgetBalanceChartInfo: {
            balance: budgetBalance,
            percent: getBudgetBalanceChartWidth(),
        },
        basicRemainCostChartInfo: {
            balance: basicRemainCost,
            percent: getBasicRemainCostChartWidth(),
        },
        differenceChartInfo: {
            balance: differenceSum,
            percent: getDiffChartWidth(),
        },
    };
};
